import { useState, useEffect, useRef, useCallback } from "react";
import {
  createQuestionEntry,
  createAnswerEntry,
  formatUserNowTime,
} from "../utils/chatHelpers";
import { isValidUUID, isValidThreadId, getUserUuid } from "../utils/auth";
import {
  parseLinksIntoCitations,
  hasMarkdownLinks,
} from "../utils/citationParser";
import { getLastCaseConversations, createThread } from "../../../../api/chat";
import { BASE_URL } from "../../../../api/config";

const deleteThread = async (threadId) => {
  const user = localStorage.getItem("user");
  return fetch(BASE_URL + "chat/deleteThread", {
    method: "POST",
    headers: { "content-type": "application/json", "x-access-token": user },
    body: JSON.stringify({ thread_id: threadId }),
  });
};

/**
 * Core chat streaming hook.
 *
 * Manages: messages, streaming state, abort/stop, retry, sent history,
 * and thread history loading.
 *
 * @param {object} opts
 * @param {string|null} opts.currentThreadId
 * @param {Function} opts.setCurrentThreadId
 * @param {Function} opts.createNewThread
 * @param {Function} opts.ensureThreadExists
 * @param {Function} opts.loadThreads
 * @param {string[]} opts.localThreadIds
 * @param {boolean} opts.speechMode
 * @param {Function} opts.getReadyDocIds - returns array of uploaded doc IDs
 * @param {Function} opts.clearAttachedFiles
 * @param {string} opts.threadType - "private" or "public"
 * @param {string} opts.selectedModel - LiteLLM model key for public chat
 * @param {number} opts.historyDepth - Number of Q+A pairs to include in context
 */
const useChatStream = ({
  currentThreadId,
  setCurrentThreadId,
  createNewThread,
  ensureThreadExists,
  loadThreads,
  localThreadIds,
  speechMode,
  getReadyDocIds,
  clearAttachedFiles,
  threadType = "private",
  selectedModel = "",
  historyDepth = 5,
}) => {
  const [chatEntry, setChatEntry] = useState("");
  const [messages, setMessages] = useState([]);
  const [sentHistory, setSentHistory] = useState([]);
  const [showLoading, setShowLoading] = useState(false);
  const [streamingText, setStreamingText] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingCitations, setStreamingCitations] = useState([]);
  const [statusText, setStatusText] = useState("");

  const abortRef = useRef(null);
  const pendingMsgRef = useRef("");
  const streamContextRef = useRef(null); // { threadId, isFirstMessage }
  const controlBufRef = useRef("");
  const messagesRef = useRef(messages);
  const threadIdRef = useRef(currentThreadId);
  const localThreadIdsRef = useRef(localThreadIds);
  const sendPayloadRef = useRef(null);
  const skipNextThreadWipeRef = useRef(false);
  const collaborativeStreamRef = useRef({
    documentId: null,
    text: "",
    agent: "",
  });
  const [threadLocked, setThreadLocked] = useState(false);
  const [threadLockedByName, setThreadLockedByName] = useState("");
  const [cancelDocumentId, setCancelDocumentId] = useState(null);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    threadIdRef.current = currentThreadId;
  }, [currentThreadId]);

  useEffect(() => {
    localThreadIdsRef.current = localThreadIds;
  }, [localThreadIds]);

  /* ─────────────── Update helpers ─────────────── */

  const updateEntry = useCallback((entryId, patch) => {
    setMessages((prev) =>
      prev.map((m) => (m.id === entryId ? { ...m, ...patch } : m)),
    );
  }, []);

  const handleToggleLike = useCallback(
    (entry) => {
      const newLike = !entry.like;
      updateEntry(entry.id, { like: newLike, dislike: false });
      // API call is handled by the caller (MainChat) via the returned updateEntry
    },
    [updateEntry],
  );

  const handleToggleDislike = useCallback(
    (entry) => {
      const newDislike = !entry.dislike;
      updateEntry(entry.id, { dislike: newDislike, like: false });
    },
    [updateEntry],
  );

  const handleToggleCatalog = useCallback(
    (entry) => {
      updateEntry(entry.id, { catalog: !entry.catalog });
    },
    [updateEntry],
  );

  /* ─────────────── Stop / Abort ─────────────── */

  const handleStop = useCallback(() => {
    // 1. Abort the fetch stream
    abortRef.current?.abort();
    abortRef.current = null;

    // 2. Restore the user's message to the input
    const msg = pendingMsgRef.current;
    if (msg) {
      setChatEntry(msg);
      pendingMsgRef.current = "";
    }

    // 3. If this was the first message on a new thread, delete the orphan
    const ctx = streamContextRef.current;
    streamContextRef.current = null;

    if (ctx?.isFirstMessage && ctx?.threadId) {
      deleteThread(ctx.threadId)
        .then(() => loadThreads())
        .catch((e) => console.error("deleteThread failed:", e));
      setCurrentThreadId(null);
    }

    // 4. Remove the question entry we pushed (last question in messages)
    setMessages((prev) => {
      const lastQIdx = prev.map((m) => m.type).lastIndexOf("question");
      if (lastQIdx === -1) return prev;
      return prev.slice(0, lastQIdx);
    });

    // 5. Clean up streaming state
    setIsStreaming(false);
    setShowLoading(false);
    setStreamingText("");
    setStreamingCitations([]);
    setStatusText("");
  }, [loadThreads, setCurrentThreadId]);

  /* ─────────────── Retry ─────────────── */

  const handleRetry = useCallback((entry) => {
    if (!entry.retryPayload) return;
    const {
      message,
      agent: retryAgent,
      threadId: retryTid,
    } = entry.retryPayload;
    setMessages((prev) => prev.filter((m) => m.id !== entry.id));
    sendPayloadRef.current?.(message, retryAgent, retryTid);
  }, []);

  /* ─────────────── sendPayload ─────────────── */

  const sendPayload = async (message, agent, maybeThreadId) => {
    const msg = message?.trim();
    if (!msg) return;

    let tid = isValidThreadId(maybeThreadId)
      ? maybeThreadId
      : isValidThreadId(threadIdRef.current)
        ? threadIdRef.current
        : null;

    if (!tid) {
      skipNextThreadWipeRef.current = true;
      tid = createNewThread();
      threadIdRef.current = tid;
    }

    const currentLen = messagesRef.current.length;
    const threadAge = currentLen <= 1 ? "new" : "old";

    streamContextRef.current = {
      threadId: tid,
      isFirstMessage: threadAge === "new",
    };

    abortRef.current?.abort();
    const abortController = new AbortController();
    abortRef.current = abortController;

    try {
      pendingMsgRef.current = msg;
      setChatEntry("");
      setSentHistory((prev) => [msg, ...prev].slice(0, 50));

      setMessages((prev) => [...prev, createQuestionEntry(msg)]);
      setShowLoading(true);

      ensureThreadExists(tid, msg, threadAge);

      const user = localStorage.getItem("user");
      const isPublic = threadType === "public";

      let fetchUrl;
      let fetchBody;

      if (isPublic) {
        fetchUrl = BASE_URL + "chat/postPublicChatMsg";
        fetchBody = {
          question: msg,
          chatThread: tid,
          model: selectedModel,
          history_depth: historyDepth,
        };
      } else {
        const docIds_attached = getReadyDocIds();
        fetchUrl = BASE_URL + "chat/postNewChatMsg";
        fetchBody = {
          question: msg,
          chatThread: tid,
          LLM: agent,
          doc_ids: docIds_attached.join(","),
          daysAgo: "365",
          domain: "All Domains",
          audio: speechMode,
        };
      }

      const response = await fetch(fetchUrl, {
        method: "POST",
        headers: {
          "content-type": "application/json",
          "x-access-token": user,
        },
        body: JSON.stringify(fetchBody),
        signal: abortController.signal,
      });

      // Clear attachments after sending (private only — public has no attachments)
      if (!isPublic) clearAttachedFiles();

      if (!response.ok || !response.body) {
        setShowLoading(false);
        throw new Error(`HTTP error: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      let output = "";
      let docIds = [];
      let documentId = "";
      let sources = [];
      let answerType = "";
      let citations = [];
      let relatedSources = [];

      controlBufRef.current = "";

      const appendText = (t) => {
        if (!t) return;
        output += t;
        setShowLoading(false);
        setStatusText("");
        setIsStreaming(true);

        // For public chat: parse markdown links live during streaming
        // so citation pills appear as soon as a link is complete
        if (isPublic && output.includes("](http")) {
          const parsed = parseLinksIntoCitations(output);
          setStreamingText(parsed.cleanedText);
          if (parsed.citations.length > 0) {
            setStreamingCitations(parsed.citations);
          }
        } else {
          setStreamingText(output);
        }
      };

      let streamError = "";

      const tryConsumeControlJson = (maybeJson) => {
        const trimmed = maybeJson.trim();

        const isControl =
          trimmed.startsWith('{"document_id"') ||
          trimmed.startsWith('{"sources"') ||
          trimmed.startsWith('{"status"') ||
          trimmed.startsWith('{"error"');

        if (!isControl) return { consumed: false };

        try {
          const obj = JSON.parse(trimmed);

          if (trimmed.startsWith('{"error"')) {
            streamError = obj["error"] || "Something went wrong.";
            setStatusText("");
            return { consumed: true };
          }

          if (trimmed.startsWith('{"status"')) {
            setStatusText(obj["status"] || "");
            return { consumed: true };
          }

          if (trimmed.startsWith('{"document_id"')) {
            let docidsString = obj["doc_ids"] || "";
            if (typeof docidsString === "string") {
              docidsString = docidsString.trim();
              if (docidsString.startsWith("[")) {
                try {
                  docIds = JSON.parse(docidsString.replace(/'/g, '"'));
                } catch {
                  docIds = [];
                }
              } else {
                docIds = docidsString
                  ? docidsString
                      .split(",")
                      .map((s) => s.trim())
                      .filter(Boolean)
                  : [];
              }
            } else if (Array.isArray(docidsString)) {
              docIds = docidsString;
            } else {
              docIds = [];
            }
            documentId = obj["document_id"] || "";
            answerType = obj["answer_type"] || "";
            citations = obj["citations"] || [];
            relatedSources = obj["related_sources"] || [];
            if (citations.length > 0) {
              setStreamingCitations(citations);
            }
          } else if (trimmed.startsWith('{"sources"')) {
            const src = obj["sources"];
            sources = src
              ? String(src)
                  .split(",")
                  .map((s) => s.trim())
                  .filter(Boolean)
              : [];
          }

          return { consumed: true };
        } catch {
          return { consumed: false, needsBuffer: true };
        }
      };

      const isControlStart = (s) =>
        s.startsWith('{"document_id"') ||
        s.startsWith('{"sources"') ||
        s.startsWith('{"status"') ||
        s.startsWith('{"error"');

      const processChunk = (raw) => {
        // Fast path: no control JSON at all
        if (!raw.includes('{"')) {
          appendText(raw);
          return;
        }

        const lines = raw.split("\n");
        let textAccum = "";

        for (let i = 0; i < lines.length; i++) {
          const t = lines[i].trim();

          if (t && isControlStart(t)) {
            if (textAccum) {
              appendText(textAccum);
              textAccum = "";
            }
            const res = tryConsumeControlJson(t);
            if (!res.consumed) {
              controlBufRef.current = t;
            }
          } else {
            if (textAccum.length > 0) textAccum += "\n";
            textAccum += lines[i];
          }
        }

        if (textAccum) {
          appendText(textAccum);
        }
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });

        if (controlBufRef.current) {
          controlBufRef.current += chunk;

          if (controlBufRef.current.includes("\n")) {
            const buffered = controlBufRef.current;
            controlBufRef.current = "";
            processChunk(buffered);
          }
          continue;
        }

        processChunk(chunk);
      }

      const tail = decoder.decode();
      if (tail || controlBufRef.current) {
        const final = (controlBufRef.current || "") + (tail || "");
        controlBufRef.current = "";
        processChunk(final);
      }

      setIsStreaming(false);
      setStreamingText("");
      setStreamingCitations([]);
      setStatusText("");

      if (streamError) {
        setMessages((prev) => [
          ...prev,
          createAnswerEntry(streamError, agent, {
            isError: true,
            retryPayload: { message: msg, agent, threadId: tid },
          }),
        ]);
      } else if (!output.trim()) {
        setMessages((prev) => [
          ...prev,
          createAnswerEntry(
            "No response received. The model may be unavailable.",
            agent,
            {
              isError: true,
              retryPayload: { message: msg, agent, threadId: tid },
            },
          ),
        ]);
      } else {
        // For public chat: parse markdown links into citation pills
        let finalOutput = output.trimEnd();
        let finalCitations = citations;
        if (isPublic && hasMarkdownLinks(finalOutput)) {
          const parsed = parseLinksIntoCitations(finalOutput);
          finalOutput = parsed.cleanedText;
          finalCitations = parsed.citations;
        }

        setMessages((prev) => [
          ...prev,
          createAnswerEntry(finalOutput, agent, {
            docIds,
            sources,
            documentId,
            answerType,
            citations: finalCitations,
            relatedSources,
            freshStream: true,
          }),
        ]);
      }

      // Update last_chat timestamp for existing threads
      if (tid && threadAge !== "new") {
        createThread(tid, "", "old", formatUserNowTime(), threadType)
          .then(() => loadThreads())
          .catch((e) => console.error("last_chat update failed:", e));
      }
    } catch (err) {
      if (err.name !== "AbortError") {
        console.error("sendPayload error:", err);
        setMessages((prev) => [
          ...prev,
          createAnswerEntry("Something went wrong. Please try again.", agent, {
            isError: true,
            retryPayload: { message: msg, agent, threadId: tid },
          }),
        ]);
      }
    } finally {
      pendingMsgRef.current = "";
      streamContextRef.current = null;
      setIsStreaming(false);
      setShowLoading(false);
      setStreamingText("");
      setStreamingCitations([]);
      setStatusText("");
    }
  };
  sendPayloadRef.current = sendPayload;

  /* ─────────────── Collaborative stream + input lock ─────────────────── */

  useEffect(() => {
    const myUuid = getUserUuid();

    const handleStreamStart = (e) => {
      const data = e.detail;
      if (data.sender_uuid === myUuid) return;
      if (data.thread_id !== threadIdRef.current) return;

      collaborativeStreamRef.current = {
        documentId: data.document_id,
        text: "",
        agent: data.agent || "",
      };

      // Lock the input
      setThreadLocked(true);
      setThreadLockedByName(data.sender_name || "Someone");
      setCancelDocumentId(data.document_id);

      // Show the collaborator's question
      setMessages((prev) => [...prev, createQuestionEntry(data.question)]);
      setShowLoading(false);
      setIsStreaming(true);
      setStreamingText("");
    };

    const handleStreamChunk = (e) => {
      const data = e.detail;
      if (data.document_id !== collaborativeStreamRef.current.documentId)
        return;

      collaborativeStreamRef.current.text += data.token;
      setStreamingText(collaborativeStreamRef.current.text);
    };

    const handleStreamEnd = (e) => {
      const data = e.detail;
      if (data.document_id !== collaborativeStreamRef.current.documentId)
        return;

      const { agent } = collaborativeStreamRef.current;

      setMessages((prev) => [
        ...prev,
        createAnswerEntry(data.message, agent, {
          documentId: data.document_id,
          answerType: data.answer_type || "text",
          citations: data.citations || [],
          relatedSources: data.citations || [],
        }),
      ]);

      // Unlock the input
      setIsStreaming(false);
      setStreamingText("");
      setStreamingCitations([]);
      setThreadLocked(false);
      setThreadLockedByName("");
      setCancelDocumentId(null);
      collaborativeStreamRef.current = {
        documentId: null,
        text: "",
        agent: "",
      };
    };

    const handleStreamCancelled = (e) => {
      const data = e.detail;
      if (data.document_id !== collaborativeStreamRef.current.documentId)
        return;

      // Clean up streaming state — the sender aborted
      setIsStreaming(false);
      setStreamingText("");
      setStreamingCitations([]);
      setThreadLocked(false);
      setThreadLockedByName("");
      setCancelDocumentId(null);
      collaborativeStreamRef.current = {
        documentId: null,
        text: "",
        agent: "",
      };
    };

    window.addEventListener("chat:stream-start", handleStreamStart);
    window.addEventListener("chat:stream-chunk", handleStreamChunk);
    window.addEventListener("chat:stream-end", handleStreamEnd);
    window.addEventListener("chat:stream-cancelled", handleStreamCancelled);

    return () => {
      window.removeEventListener("chat:stream-start", handleStreamStart);
      window.removeEventListener("chat:stream-chunk", handleStreamChunk);
      window.removeEventListener("chat:stream-end", handleStreamEnd);
      window.removeEventListener(
        "chat:stream-cancelled",
        handleStreamCancelled,
      );
    };
  }, []);

  /* ─────────────── Thread change → load history ─────────────── */

  useEffect(() => {
    if (skipNextThreadWipeRef.current) {
      skipNextThreadWipeRef.current = false;
      return;
    }

    setMessages([]);
    setStreamingText("");
    setIsStreaming(false);
    setShowLoading(false);
    setStreamingCitations([]);
    setStatusText("");
    setThreadLocked(false);
    setThreadLockedByName("");
    setCancelDocumentId(null);
    collaborativeStreamRef.current = { documentId: null, text: "", agent: "" };

    if (!currentThreadId || !isValidUUID(currentThreadId)) return;
    if ((localThreadIdsRef.current || []).includes(currentThreadId)) return;

    getLastCaseConversations(currentThreadId)
      .then((response) => {
        const loaded = [];
        response.data.forEach((item) => {
          const urlDivs = item.sources
            ? item.sources
                .split(",")
                .map((u) => u.trim())
                .filter((u) => u !== "" && u.length >= 5)
            : [];

          loaded.push(createQuestionEntry(item.question));
          loaded.push(
            createAnswerEntry(item.answer, item.agent, {
              docIds: item.doc_ids,
              sources: urlDivs,
              documentId: item.document_id,
              citations: item.citations || [],
              relatedSources: item.related_sources || [],
              like: item.like,
              dislike: item.dislike,
              catalog: item.catalog,
            }),
          );
        });
        setMessages(loaded);

        // Mid-stream rejoin: if the last message is still streaming,
        // pick up where the partial DB save left off and lock the input
        const lastItem = response.data[response.data.length - 1];
        if (lastItem?.streaming) {
          const myUuid = getUserUuid();
          collaborativeStreamRef.current = {
            documentId: lastItem.document_id,
            text: lastItem.answer || "",
            agent: lastItem.agent || "",
          };
          setIsStreaming(true);
          setStreamingText(lastItem.answer || "");

          // Lock if it's someone else's stream
          if (lastItem.streaming_by && lastItem.streaming_by !== myUuid) {
            setThreadLocked(true);
            setThreadLockedByName(lastItem.streaming_by_name || "Someone");
            setCancelDocumentId(lastItem.document_id);
          }
        }
      })
      .catch((err) => {
        console.error("Failed to load thread history:", err);
      });
  }, [currentThreadId]);

  return {
    chatEntry,
    setChatEntry,
    messages,
    setMessages,
    sentHistory,
    showLoading,
    streamingText,
    isStreaming,
    streamingCitations,
    statusText,
    sendPayload,
    handleStop,
    handleRetry,
    handleToggleLike,
    handleToggleDislike,
    handleToggleCatalog,
    updateEntry,
    threadLocked,
    setThreadLocked,
    threadLockedByName,
    cancelDocumentId,
  };
};

export default useChatStream;
